import { BaseEntity, isEntityOfType } from ".";
import { getCachedGroupEntity } from "./GroupEntityCache";
import { getCachedTypeEntity } from "./TypeEntityCache";

export class BlueprintEntity extends BaseEntity {
  constructor(id: number, props: any) {
    super(id, props);
    this._dataTypeName = "Blueprint";
  }

  async toJson(): Promise<Excel.EntityCellValue> {
    const props: any = this.props;

    var entity = await super.toJson();

    // Properties
    if (entity.properties) {
      // - Type properties
      if (Object.prototype.hasOwnProperty.call(props, "blueprintTypeID")) {
        const blueprintTypeEntity = await getCachedTypeEntity(props["blueprintTypeID"]);
        if (entity.properties && blueprintTypeEntity !== null) {
          // - Name
          entity.properties["name"] = {
            type: Excel.CellValueType.string,
            basicValue: blueprintTypeEntity.props["name"],
          };
          entity.text = blueprintTypeEntity.props["name"];
          // - Base Price
          if (Object.prototype.hasOwnProperty.call(blueprintTypeEntity.props, "basePrice")) {
            entity.properties["basePrice"] = {
              type: Excel.CellValueType.double,
              basicValue: blueprintTypeEntity.props.basePrice === "None" ? 0 : blueprintTypeEntity.props.basePrice,
              propertyMetadata: {
                sublabel: "ISK",
              },
            };
          }
        }
        // - Blueprint Type Id
        delete entity.properties["blueprintTypeID"];
      }
      // - Activities
      if (Object.prototype.hasOwnProperty.call(props, "activities")) {
        const activities = ["copying", "invention", "manufacturing", "reaction", "research_material", "research_time"];
        for (let i = 0; i < activities.length; i++) {
          const activity = activities[i];
          if (entity.properties && Object.prototype.hasOwnProperty.call(props.activities, activity)) {
            entity.properties[activity] = await makeActivityEntity(props.activities[activity], activity);
          }
        }
      }
    }

    // Layouts
    entity.layouts = {
      card: {
        title: { property: "name" },
      },
    };
    return entity;
  }
}

async function makeActivityEntity(activity: any, name: string): Promise<Excel.EntityCellValue> {
  let entity: Excel.EntityCellValue = {
    type: Excel.CellValueType.entity,
    text: `(${name})`,
    properties: {},
  };

  let entityTextSeparator = "";
  let entityText = "";

  // Properties
  if (entity.properties) {
    // - Materials
    if (Object.prototype.hasOwnProperty.call(activity, "materials")) {
      let elements: Excel.CellValue[][] = [];
      for (let i = 0; i < activity.materials.length; i++) {
        const material = activity.materials[i];
        const typeEntity = await getCachedTypeEntity(material.typeID);
        if (typeEntity != null) {
          const _groupID = typeEntity.props.groupID;
          const cat = await getCachedGroupEntity(_groupID);
          const _categoryID = cat?.props.categoryID ?? -1;
          elements.push([
            {
              type: Excel.CellValueType.entity,
              text: `${typeEntity.props["name"]} x ${material.quantity}`,
              properties: {
                type: await typeEntity.toJson(),
                quantity: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: material.quantity,
                  numberFormat: "##0",
                },
                type_group_id: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: _groupID,
                  numberFormat: "##0",
                },
                type_category_id: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: _categoryID,
                  numberFormat: "##0",
                },
              },
            },
          ]);
        }
      }
      if (elements.length > 0) {
        entity.properties["materials"] = {
          type: Excel.CellValueType.array,
          elements: elements,
        };
        entityText = entityText.concat(
          `${entityTextSeparator}${elements.length} ${elements.length === 1 ? "material" : "materials"}`
        );
        entityTextSeparator = ", ";
      }
    }
    // - Skills
    if (Object.prototype.hasOwnProperty.call(activity, "skills")) {
      let elements: Excel.CellValue[][] = [];
      for (let i = 0; i < activity.skills.length; i++) {
        const skill = activity.skills[i];
        const typeEntity = await getCachedTypeEntity(skill.typeID);
        if (typeEntity != null) {
          const level = skill.level || 0;
          elements.push([
            {
              type: Excel.CellValueType.entity,
              text: `${typeEntity.props["name"]} Level ${level}`,
              properties: {
                skill: await typeEntity.toJson(),
                level: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: level,
                  numberFormat: "0",
                },
              },
            },
          ]);
        }
      }
      if (elements.length > 0) {
        entity.properties["skills"] = {
          type: Excel.CellValueType.array,
          elements: elements,
        };
        entityText = entityText.concat(
          `${entityTextSeparator}${elements.length} ${elements.length === 1 ? "skill" : "skills"}`
        );
        entityTextSeparator = ", ";
      }
    }
    // - Products
    if (Object.prototype.hasOwnProperty.call(activity, "products")) {
      let elements: Excel.CellValue[][] = [];
      for (let i = 0; i < activity.products.length; i++) {
        const product = activity.products[i];
        const typeEntity = await getCachedTypeEntity(product.typeID);
        if (typeEntity != null) {
          const probability = product.probability || 1;
          const quantity = product.quantity || 1;
          elements.push([
            {
              type: Excel.CellValueType.entity,
              text: `${typeEntity.props["name"]} x ${quantity} x ${probability * 100}%`,
              properties: {
                type: await typeEntity.toJson(),
                quantity: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: quantity,
                  numberFormat: "##0",
                },
                probability: {
                  type: Excel.CellValueType.formattedNumber,
                  basicValue: probability,
                  numberFormat: "##0%",
                },
              },
            },
          ]);
        }
      }
      if (elements.length > 0) {
        entity.properties["products"] = {
          type: Excel.CellValueType.array,
          elements: elements,
        };
        entityText = entityText.concat(
          `${entityTextSeparator}${elements.length} ${elements.length === 1 ? "product" : "products"}`
        );
        entityTextSeparator = ", ";
      }
    }
    // - Time
    if (Object.prototype.hasOwnProperty.call(activity, "time")) {
      entity.properties["time"] = {
        type: Excel.CellValueType.formattedNumber,
        basicValue: activity.time,
        numberFormat: "##0",
      };
      entityText = entityText.concat(`${entityTextSeparator}time ${activity.time}`);
      entityTextSeparator = ", ";
    }
  }
  // - Text
  entity.text = entityText !== "" ? entityText : activity;
  return entity;
}

export function isBlueprintEntity(entity: any): boolean {
  return isEntityOfType(entity, "Blueprint");
}
